/**
 * RowStatusIndicator — FT-349e (extended by FT-353a)
 *
 * Unified per-row indicator badge for history views. Renders lucide icons for
 * the four common per-row signals: photos (with count), notes, defects, signed.
 *
 * FT-349 is the first consumer (MaintenanceHistoryView). Other history views
 * (Defects, Drills, Incidents) can adopt without touching this file.
 *
 * FT-353a adds a `jobId` prop so Defects rows can flag "this defect has a
 * linked corrective maintenance job" with a Wrench chip. Display-only for
 * now — clickthrough lands with FT-353b.
 *
 * API:
 *   <RowStatusIndicator
 *     hasPhotos={boolean}          // Camera icon with count
 *     photoCount={number}           // required when hasPhotos=true
 *     hasNotes={boolean}            // FileText icon
 *     hasDefects={boolean}          // AlertTriangle icon
 *     signed={boolean}              // CheckCircle icon
 *     jobId={string|null}           // FT-353a — Wrench chip w/ short job id
 *     size="sm" | "md"              // default "sm"
 *     variant="inline" | "cell"     // default "inline" ("cell" wraps in <td>)
 *     className={string}            // pass-through
 *     testIdPrefix={string}         // default 'row-status'
 *   />
 *
 * If ALL flags are false/0 AND jobId is empty → renders em-dash placeholder.
 */

import React from 'react';
import { Camera, FileText, AlertTriangle, CheckCircle, Wrench } from 'lucide-react';

// FT-353a: shorten a job id like "JOB-abc12345" → "abc12" (last 5 chars of the
// significant portion). Falls back to raw id if it's already short.
const shortenJobId = (jobId) => {
  if (!jobId || typeof jobId !== 'string') return '';
  const trimmed = jobId.trim();
  if (trimmed.length <= 5) return trimmed;
  return trimmed.slice(-5);
};

const RowStatusIndicator = ({
  hasPhotos = false,
  photoCount = 0,
  hasNotes = false,
  hasDefects = false,
  signed = false,
  jobId = null,
  size = 'sm',
  variant = 'inline',
  className = '',
  testIdPrefix = 'row-status',
}) => {
  const hasJob = typeof jobId === 'string' && jobId.trim().length > 0;
  const anyActive =
    (hasPhotos && photoCount > 0) || hasNotes || hasDefects || signed || hasJob;

  // Size-based icon/number classes
  const iconClass = size === 'md' ? 'h-4 w-4' : 'h-3 w-3';
  const numberClass = size === 'md' ? 'text-sm' : 'text-xs';

  // Empty state
  const content = !anyActive ? (
    <span className="text-gray-300" data-testid={`${testIdPrefix}-empty`}>—</span>
  ) : (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {hasPhotos && photoCount > 0 && (
        <span
          className="inline-flex items-center gap-0.5 text-cyan-600"
          data-testid={`${testIdPrefix}-camera`}
          title={`${photoCount} photo${photoCount === 1 ? '' : 's'}`}
        >
          <Camera className={iconClass} />
          <span className={`${numberClass} font-medium`}>{photoCount}</span>
        </span>
      )}
      {hasNotes && (
        <FileText
          className={`${iconClass} text-slate-500`}
          data-testid={`${testIdPrefix}-notes`}
        />
      )}
      {hasDefects && (
        <AlertTriangle
          className={`${iconClass} text-amber-600`}
          data-testid={`${testIdPrefix}-defects`}
        />
      )}
      {signed && (
        <CheckCircle
          className={`${iconClass} text-emerald-600`}
          data-testid={`${testIdPrefix}-signed`}
        />
      )}
      {/* FT-353a: corrective-job chip. Wrapped in a <button> so a future
          ticket (FT-353b) can attach onClick without a DOM shape change.
          Display-only for now — no navigation. type="button" prevents
          accidental form submits when nested inside forms. The chip does
          NOT stopPropagation so the parent row's onClick (open detail view)
          still fires when the user taps the chip area. */}
      {hasJob && (
        <button
          type="button"
          className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-slate-600 hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-400"
          data-testid={`${testIdPrefix}-job-chip`}
          data-job-id={jobId}
          title={`Linked corrective job ${jobId}`}
        >
          <Wrench className={`${iconClass} text-amber-500`} />
          <span className={`${numberClass} font-medium`}>
            {shortenJobId(jobId)}
          </span>
        </button>
      )}
    </div>
  );

  if (variant === 'cell') {
    return (
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        {content}
      </td>
    );
  }

  return content;
};

export default RowStatusIndicator;
